import { useTranslation } from "react-i18next";
import { useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";
import type React from "react";

const NAV_KEYS = [
  { key: "nav_home", href: "#home" },
  { key: "nav_about", href: "#about" },
  { key: "nav_services", href: "#services" },
  { key: "nav_flow", href: "#flow" },
  { key: "nav_testimonials", href: "#testimonials" },
  { key: "nav_faq", href: "#faq" },
  { key: "nav_contact", href: "#contact" },
];

export default function Footer() {
  const { t } = useTranslation();
  const { setRef, visibleItems } = useScrollAnimationMultiple(3, { threshold: 0.05 });

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-[#0d2b2a] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
          {/* Brand */}
          <div
            ref={setRef(0) as unknown as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ease-out ${visibleItems[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_3693c096081961b489aaeeec35ce6ce4_ea61dad8.jpg"
                alt="てんてんおじさん鑑定所"
                className="h-12 w-auto object-contain"
                loading="lazy"
                decoding="async"
              />
              <div>
                <p className="text-xs text-[#0ABAB5] tracking-widest">{t("footer_subtitle")}</p>
                <p className="text-base font-bold leading-tight">{t("footer_title")}</p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">{t("footer_desc")}</p>
          </div>

          {/* Contact Info */}
          <div
            ref={setRef(1) as unknown as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ease-out ${visibleItems[1] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ transitionDelay: visibleItems[1] ? "150ms" : "0ms" }}
          >
            <h4 className="text-xs font-bold tracking-widest text-[#0ABAB5] mb-6">{t("footer_contact_title")}</h4>
            <div className="space-y-5">
              <div>
                <p className="text-xs text-white/40 font-medium tracking-widest mb-1">{t("footer_address_label")}</p>
                <p className="text-white/80 text-sm whitespace-pre-line">{t("footer_address_value")}</p>
              </div>
              <div>
                <p className="text-xs text-white/40 font-medium tracking-widest mb-1">{t("footer_email_label")}</p>
                <p className="text-white/80 text-sm">tenten.sensei.okinawa@gmail.com</p>
              </div>
              <div>
                <p className="text-xs text-white/40 font-medium tracking-widest mb-1">{t("footer_hours_label")}</p>
                <p className="text-white/80 text-sm whitespace-pre-line">{t("footer_hours_value")}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div
            ref={setRef(2) as unknown as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ease-out ${visibleItems[2] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ transitionDelay: visibleItems[2] ? "300ms" : "0ms" }}
          >
            <h4 className="text-xs font-bold tracking-widest text-[#0ABAB5] mb-6">{t("footer_nav_title")}</h4>
            <ul className="space-y-3">
              {NAV_KEYS.map((item) => (
                <li key={item.href}>
                  <button
                    onClick={() => handleNav(item.href)}
                    className="text-white/60 text-sm hover:text-[#0ABAB5] transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-[#0ABAB5] text-xs" />
                    {t(item.key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs">{t("footer_copyright")}</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            {[
              { key: "footer_privacy" },
              { key: "footer_law" },
              { key: "footer_sitemap" },
            ].map((link) => (
              <a key={link.key} href="#" rel="nofollow" className="text-white/40 text-xs hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">
                {t(link.key)}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
